import React, { useEffect, useMemo, useRef, useState } from "react";

import moment from "moment";

/** PrimeReact Components */
import { Button } from "primereact/button";
import FullCalendar from "@fullcalendar/react";
import { Skeleton } from "primereact/skeleton";
import { InputText } from "primereact/inputtext";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";

/** Enums */
import UserRoles from "../../../enums/UserRoles.ENUM";

/** RTK Query */
import { useLazyGetDataQuery } from "../../../app/services/commonApi";

/** Utilities */
import { getAuthProps } from "../../../utils/authentication/authLibrary";

/** CSS */
import "./InterviewSlotsCalendar.scss";

const InterviewSlotsCalendar = () => {
  const calendarRef = useRef(null);
  const containerRef = useRef(null);
  const authData = getAuthProps();
  const userRoleId = authData?.roles?.roleId || authData?.user?.roleId;
  const currentUserId = authData?.user?.userId;
  const organizationId = authData?.organizationId;

  const isHRAdmin = [UserRoles.HR, UserRoles.ADMIN].includes(userRoleId);

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [triggerGetData] = useLazyGetDataQuery();

  const fetchSlots = async () => {
    setLoading(true);
    setSelectedEvent(null);
    try {
      if (!isHRAdmin) {
        const response = await triggerGetData({
          url: `/InterviewerCalendar/GetAvailableSlots`,
          data: { interviewerId: currentUserId, organizationId },
        }).unwrap();

        setSlots(response?.data || []);
      } else {
        const interviewersRes = await triggerGetData({
          url: "/UserManagement/GetAllUsersByRoleId",
          data: UserRoles.INTERVIEWER,
        }).unwrap();

        const interviewers = interviewersRes?.data || [];

        if (interviewers.length === 0) {
          setSlots([]);
          setLoading(false);
          return;
        }

        const fetchPromises = interviewers.map(async (interviewer) => {
          try {
            const slotsRes = await triggerGetData({
              url: `/InterviewerCalendar/GetAvailableSlots`,
              data: { interviewerId: interviewer.userId, organizationId },
            }).unwrap();

            const interviewerSlots = slotsRes?.data || [];

            return interviewerSlots.map((slot) => ({
              ...slot,
              interviewerName: `${interviewer.firstName} ${interviewer.lastName}`.trim(),
            }));
          } catch (err) {
            console.error(`Error fetching slots for interviewer ${interviewer.userId}:`, err);
            return [];
          }
        });

        const allResults = await Promise.all(fetchPromises);
        setSlots(allResults.flat());
      }
    } catch (error) {
      console.error("Error loading interview slots:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlots();
  }, [userRoleId, currentUserId]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let popoverObserver = null;

    const adjustPopover = (popover) => {
      if (!popover || !container) return;

      const popoverRect = popover.getBoundingClientRect();
      const calendarRect = container.getBoundingClientRect();

      if (popoverRect.right > calendarRect.right) {
        const overflow = popoverRect.right - calendarRect.right;
        const currentLeft = parseFloat(popover.style.left) || popover.offsetLeft || 0;
        popover.style.setProperty("left", `${currentLeft - overflow - 12}px`, "important");
      }
      else if (popoverRect.left < calendarRect.left) {
        const overflow = calendarRect.left - popoverRect.left;
        const currentLeft = parseFloat(popover.style.left) || popover.offsetLeft || 0;
        popover.style.setProperty("left", `${currentLeft + overflow + 12}px`, "important");
      }
    };

    const observer = new MutationObserver(() => {
      const popover = container.querySelector(".fc-more-popover");
      if (popover) {
        adjustPopover(popover);

        if (!popoverObserver) {
          popoverObserver = new MutationObserver(() => {
            popoverObserver.disconnect();
            adjustPopover(popover);
            popoverObserver.observe(popover, { attributes: true, attributeFilter: ["style"] });
          });
          popoverObserver.observe(popover, { attributes: true, attributeFilter: ["style"] });
        }
      } else if (popoverObserver) {
        popoverObserver.disconnect();
        popoverObserver = null;
      }
    });

    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (popoverObserver) {
        popoverObserver.disconnect();
      }
    };
  }, []);

  const calendarEvents = useMemo(() => {
    return slots.map((slot) => {
      const startTimeFormatted = slot?.startTime
        ? moment(slot.startTime).format("hh:mm A")
        : "";
      const endTimeFormatted = slot?.endTime
        ? moment(slot.endTime).format("hh:mm A")
        : "";

      const title = isHRAdmin
        ? `${slot.interviewerName || "Interviewer"} (${startTimeFormatted})`
        : `${startTimeFormatted} - ${endTimeFormatted}`;

      return {
        id: slot.slotId?.toString(),
        title: title,
        start: slot.startTime,
        end: slot.endTime,
        className: "fc-event-available",
        extendedProps: {
          interviewerName: slot.interviewerName,
          startTimeFormatted,
          endTimeFormatted,
        },
      };
    });
  }, [slots, isHRAdmin]);

  const filteredEvents = useMemo(() => {
    if (!isHRAdmin || !searchQuery.trim()) {
      return calendarEvents;
    }
    const query = searchQuery.toLowerCase();
    return calendarEvents.filter((event) =>
      event.extendedProps?.interviewerName?.toLowerCase().includes(query)
    );
  }, [calendarEvents, searchQuery, isHRAdmin]);

  const handleEventClick = (info) => {
    const event = info.event;
    setSelectedEvent({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      interviewerName: event.extendedProps?.interviewerName,
      startTimeFormatted: event.extendedProps?.startTimeFormatted,
      endTimeFormatted: event.extendedProps?.endTimeFormatted,
    });
  };

  return (
    <div ref={containerRef} className="interview-slots-container">
      <div className="calendar-header-actions">
        <h4 className="slots-title">Interview Available Slots</h4>

        {isHRAdmin && slots.length > 0 && (
          <div className="search-filter-wrapper">
            <span className="p-input-icon-left w-full">
              <i className="pi pi-search" />
              <InputText
                type="text"
                placeholder="Filter by Interviewer"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </span>
          </div>
        )}

        <Button
          icon="pi pi-refresh"
          className="btn secondary-btn reload-btn ml-auto"
          onClick={fetchSlots}
          loading={loading}
          tooltip="Refresh"
          data-pr-position="top"
        />
      </div>

      <div className="calendar-section">
        {loading ? (
          <div className="skeleton-calendar-loader">
            <Skeleton height="150px" borderRadius="8px" />
          </div>
        ) : (
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: "prev,next",
              center: "title",
              right: "",
            }}
            events={filteredEvents}
            eventClick={handleEventClick}
            height="500px"
            dayMaxEvents={2}
            displayEventTime={false}
            showNonCurrentDates={false}
            moreLinkClick="popover"
          />
        )}
      </div>

      {selectedEvent ? (
        <div className="slot-detail-view">
          <div className="detail-card">
            <h5 className="detail-title">Selected Slot Details</h5>
            <div className="detail-time">
              {moment(selectedEvent.start).format("DD MMM YYYY")} | {selectedEvent.startTimeFormatted} - {selectedEvent.endTimeFormatted}
            </div>
            {isHRAdmin && selectedEvent.interviewerName && (
              <div className="detail-info-row">
                <i className="pi pi-user" />
                <span>
                  Interviewer: <span className="interviewer-name">{selectedEvent.interviewerName}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        !loading && slots.length === 0 && (
          <div className="no-slots-message">
            <i className="pi pi-calendar-times" />
            <span>No available slots found</span>
          </div>
        )
      )}
    </div>
  );
};

export default InterviewSlotsCalendar;
